import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Download } from "lucide-react";
import api from "@/lib/api";
import { Overline } from "@/components/Bits";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [info, setInfo] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const attempts = useRef(0);
  const { clear } = useCart();

  const poll = useCallback(async () => {
    if (!sessionId) return setState("failed");
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === "paid") {
        setState("paid");
        clear();
        // Fetch download manifest if any books were in the order
        try {
          const { data: dl } = await api.get(`/store/download/${sessionId}`);
          setDownloads(dl.downloads || []);
        } catch { /* not a book order */ }
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) return setState("failed");
      if (attempts.current >= 8) return setState("failed");
      attempts.current += 1;
      setTimeout(poll, 2000);
    } catch {
      setState("failed");
    }
  }, [sessionId, clear]);

  useEffect(() => { poll(); }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="glass corner-frame max-w-lg w-full p-12 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="text-gold-500 mx-auto animate-spin" />
            <p className="mt-8 font-serif text-3xl text-white">Confirming your payment…</p>
            <p className="mt-3 text-white/60 text-sm">This will only take a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={52} className="text-gold-400 mx-auto" />
            <Overline className="mt-8 block">Payment Confirmed</Overline>
            <p className="mt-4 font-serif text-4xl text-white">Thank you.</p>
            <p className="mt-4 text-white/70">
              Your purchase of <span className="text-gold-300">{info?.title}</span> is complete.
              {info?.item_type === "course" && " You've been enrolled — start learning now."}
            </p>
            {downloads.length > 0 && (
              <div className="mt-8 border border-gold-500/25 bg-ink-paper/40 p-5" data-testid="download-panel">
                <Overline className="mb-4 block">Your Downloads</Overline>
                <div className="space-y-2">
                  {downloads.map((d) => (
                    <a
                      key={d.slug}
                      href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`download-${d.slug}`}
                      className="flex items-center justify-between gap-3 border border-gold-500/30 px-4 py-3 text-sm text-white hover:bg-gold-500/10 transition-colors"
                    >
                      <span className="text-left truncate">{d.title}</span>
                      <span className="inline-flex items-center gap-1.5 text-gold-300 shrink-0"><Download size={14} /> PDF</span>
                    </a>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-white/50">Bookmark this page — you can re-download from your email receipt anytime.</p>
              </div>
            )}
            <div className="mt-10 flex flex-col gap-3">
              {info?.item_type === "course" ? (
                <Link to={`/academy/${info.slug}`} className="bg-gold-500 py-3.5 text-sm font-semibold text-ink hover:bg-gold-400 transition-colors">Start Learning</Link>
              ) : (
                <Link to="/" className="bg-gold-500 py-3.5 text-sm font-semibold text-ink hover:bg-gold-400 transition-colors">Back to Home</Link>
              )}
            </div>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={52} className="text-white/60 mx-auto" />
            <p className="mt-8 font-serif text-3xl text-white">We couldn't confirm this payment.</p>
            <p className="mt-3 text-white/60 text-sm">If you were charged, please contact us and we'll sort it out.</p>
            <Link to="/contact" className="mt-8 inline-block border border-gold-500/40 px-7 py-3.5 text-sm text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors">Contact Support</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
